import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { EXERCISES } from "@/entities/exercise/model/exercises";
import { MUSCLES } from "@/entities/muscle/model/muscles";
import { MuscleDetail, type ExerciseHighlight } from "@/entities/muscle/ui/biceps-detail.ui";
import { DETAIL_PART_COLOR } from "@/entities/muscle/ui/biceps-parts";
import { inferExerciseHeads } from "@/entities/muscle/ui/exercise-head-heuristics";

const EMPTY: ExerciseHighlight = { primary: new Set(), secondary: new Set(), heads: new Set() };

const headLabel = (qualified: string): string => {
  const [group, part] = qualified.split(":");

  return `${group} · ${part}`.replaceAll("_", " ");
};

type LegendProps = Readonly<{
  exercise: (typeof EXERCISES)[number];
  heads: ReadonlySet<string>;
}>;

const HighlightLegend = ({ exercise, heads }: LegendProps) => (
  <div className="flex flex-wrap items-center gap-3 text-xs">
    <Swatch
      color="#ef4444"
      label={`Primary: ${exercise.primary.map((m) => MUSCLES[m].label).join(", ")}`}
    />
    {exercise.secondary.length > 0 && (
      <Swatch
        color="#3b82f6"
        label={`Secondary: ${exercise.secondary.map((m) => MUSCLES[m].label).join(", ")}`}
      />
    )}
    {[...heads].map((h) => (
      <Swatch key={h} color={DETAIL_PART_COLOR.get(h) ?? "#fff"} label={headLabel(h)} />
    ))}
  </div>
);

const BicepsDemoPage = () => {
  const [exerciseId, setExerciseId] = useState<string>("");

  const exercise = useMemo(() => EXERCISES.find((e) => e.id === exerciseId), [exerciseId]);

  const highlight: ExerciseHighlight = useMemo(() => {
    if (exercise === undefined) return EMPTY;

    return {
      primary: new Set(exercise.primary),
      secondary: new Set(exercise.secondary),
      heads: new Set(inferExerciseHeads(exercise.name)),
    };
  }, [exercise]);

  return (
    <div className="flex h-screen w-screen flex-col bg-neutral-900 text-white">
      <div className="flex flex-wrap items-center gap-3 border-b border-neutral-800 p-3">
        <span className="text-sm text-neutral-400">Training:</span>
        <select
          className="rounded-md bg-neutral-800 px-3 py-1.5 text-sm"
          value={exerciseId}
          onChange={(e) => setExerciseId(e.target.value)}
        >
          <option value="">Select a training…</option>
          {EXERCISES.map((e) => (
            <option key={e.id} value={e.id}>
              {e.name}
            </option>
          ))}
        </select>

        {exercise !== undefined && <HighlightLegend exercise={exercise} heads={highlight.heads} />}
      </div>
      <div className="min-h-0 flex-1">
        <MuscleDetail highlight={highlight} />
      </div>
    </div>
  );
};

const Swatch = ({ color, label }: { color: string; label: string }) => (
  <span className="flex items-center gap-1.5">
    <span className="inline-block h-3 w-3 rounded-sm" style={{ backgroundColor: color }} />
    {label}
  </span>
);

export const Route = createFileRoute("/biceps-demo")({
  component: BicepsDemoPage,
});
